import React, { useState, useEffect, useMemo } from 'react';
import { createBrowserRouter, RouterProvider, Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  FileSignature,
  Upload,
  ChevronRight,
  ChevronUp,
  Eye,
  X,
  LogOut,
  Search,
  Sun,
  Moon,
  Activity as ActivityIcon,
  Braces,
  Handshake,
  HardDrive,
  BookOpen,
  ShieldCheck,
  Users as UsersIcon,
  PanelLeft,
  Route as RouteIcon,
  MessageSquare,
  Building2,
  BookText,
} from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { ToastProvider } from './components/ToastProvider';
import Dashboard from './pages/Dashboard';
import ContractsList from './pages/ContractsList';
import CounterpartiesList from './pages/CounterpartiesList';
import CounterpartyDetail from './pages/CounterpartyDetail';
import ContractDetail from './pages/ContractDetail';
import ContractForm from './pages/ContractForm';
import UploadContract from './pages/UploadContract';
import Generate from './pages/Generate';
import ScreeningDetail from './pages/ScreeningDetail';
import RequestsList from './pages/RequestsList';
import RequestForm from './pages/RequestForm';
import RequestDetail from './pages/RequestDetail';
import DriveSettings from './pages/DriveSettings';
import Playbooks from './pages/Playbooks';
import DataSchemas from './pages/DataSchemas';
import ReviewsList from './pages/ReviewsList';
import ReviewDetail from './pages/ReviewDetail';
import AdminUsers from './pages/AdminUsers';
import AdminConversations from './pages/AdminConversations';
import AdminIntakes from './pages/AdminIntakes';
import AdminActivity from './pages/AdminActivity';
import Docs from './pages/Docs';
import DocPage from './pages/DocPage';
import CommandK from './components/CommandK';
import Login from './pages/Login';
import { API_BASE } from './config/api';
import { apiMaybe } from './services/apiClient';
import { useNarrowViewport } from './hooks/useNarrowViewport';

/* ------------------------------------------------------------------ */
/* Auth-gated routing                                                  */
/* ------------------------------------------------------------------ */

// Full-viewport spinner shown while the auth session check is in flight.
function LoadingScreen() {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: 'var(--surface-panel)',
      }}
    >
      <div className="col" style={{ alignItems: 'center', gap: 12 }}>
        <div className="w-8 h-8 border-2 rounded-full animate-spin"
             style={{ borderColor: 'var(--color-purple-500)', borderTopColor: 'transparent' }} />
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</span>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Gate a route to legal/admin only. Non-legal users are bounced to /
// rather than /login (they're authenticated; just not authorized).
function LegalRoute({ children }) {
  const { user, loading, canWriteContracts } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!canWriteContracts) return <Navigate to="/" replace />;
  return children;
}

// Gate a route on ONE capability — for admin surfaces an individual user can
// hold via a per-user grant without the rest of the admin area (doc-279: a
// data scientist with manage_schemas reaches only /admin/schemas). Same bounce
// behavior as LegalRoute; enforcement is the backend capability gate.
function CapabilityRoute({ cap, children }) {
  const { user, loading, can } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!can(cap)) return <Navigate to="/" replace />;
  return children;
}

/* ------------------------------------------------------------------ */
/* Sidebar — Origin design, CSS classes from components.css            */
/* ------------------------------------------------------------------ */

function Brand({ isDark }) {
  return (
    <Link to="/" className="sidebar-brand">
      <img
        src={isDark ? '/lightspark-logo-white.svg' : '/lightspark-logo.svg'}
        alt="Lightspark"
      />
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, lineHeight: 1.1 }}>
        <span className="sidebar-brand-text">Docket</span>
        <span
          style={{
            fontSize: 10,
            color: 'var(--text-tertiary)',
            letterSpacing: 0.2,
            marginTop: 2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          Legal &amp; Compliance Ops
        </span>
      </div>
    </Link>
  );
}

function SidebarItem({ to, icon: Icon, label, collapsed, count, exact, siblingPaths = [] }) {
  const location = useLocation();
  const path = location.pathname;
  // "Active" = exact match, OR path is nested under `to` AND no other
  // sibling nav item is a more specific match. This prevents /contracts
  // from lighting up "Contracts" when the user is actually on the more
  // specific /contracts/upload item.
  const active = exact
    ? path === to
    : path === to ||
      (path.startsWith(to + '/') &&
        !siblingPaths.some(
          (sib) => sib !== to && (path === sib || path.startsWith(sib + '/'))
        ));
  return (
    <Link
      to={to}
      className={`sidebar-item ${active ? 'active' : ''}`}
      title={collapsed ? label : undefined}
    >
      <Icon className="w-[16px] h-[16px] shrink-0" style={{ opacity: active ? 1 : 0.75 }} />
      {!collapsed && <span className="sidebar-item-label">{label}</span>}
      {!collapsed && count != null && <span className="sidebar-item-count">{count}</span>}
    </Link>
  );
}

// Tiny build stamp in the sidebar footer. Reads the backend's /api/version so
// what's shown reflects the API server you're actually talking to (the thing
// we care about when verifying a deploy). Silently renders nothing if the
// probe is unavailable or the sidebar is collapsed.
function VersionBadge({ collapsed }) {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    let alive = true;
    apiMaybe('/version').then((v) => { if (alive && v) setInfo(v); });
    return () => { alive = false; };
  }, []);

  if (collapsed || !info) return null;
  const sha = info.gitSha && info.gitSha !== 'dev'
    ? info.gitSha.slice(0, 7)
    : (info.gitSha || 'dev');
  const label = info.display ? `v${info.display}` : `v${info.version} · ${sha}`;
  return (
    <div
      title={info.builtAt ? `Built ${info.builtAt}` : 'Local dev build'}
      style={{
        padding: '8px 12px 2px',
        fontSize: 10,
        color: 'var(--text-tertiary)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        letterSpacing: 0.2,
      }}
    >
      {label}
    </div>
  );
}

// Bottom-left user menu: avatar row opens a popover holding everything that
// used to stack in the sidebar footer (role preview, version, sign out).
// Keeps the footer to a single row; the active preview gets its own slim
// pill so the simulated state stays visible while the menu is closed.
function UserMenu({
  user, initials, avatarFailed, setAvatarFailed, collapsed,
  realIsAdmin, previewRole, setPreviewRole, previewableRoles, logout, viewAs,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = React.useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      {open && (
        <div
          className="card"
          style={{
            position: 'absolute',
            zIndex: 50,
            ...(collapsed
              ? { left: 'calc(100% + 8px)', bottom: 0, width: 230 }
              : { bottom: 'calc(100% + 6px)', left: 0, right: 0 }),
            padding: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}
        >
          <div style={{ padding: '2px 6px 8px', borderBottom: '0.5px solid var(--border-primary)', marginBottom: 8 }}>
            <div className="sidebar-user-name">{user?.name || 'User'}</div>
            <div className="sidebar-user-email">{user?.email}</div>
          </div>
          {/* The two lenses never stack — while a view-as session is open the
              role selector is withheld, and the banner's exit is the only way
              back. */}
          {realIsAdmin && viewAs && (
            <div className="muted" style={{ padding: '0 6px 10px', fontSize: 11, lineHeight: 1.4 }}>
              Viewing as {viewAs.target?.name || viewAs.target?.email}. Exit view-as to preview a role.
            </div>
          )}
          {realIsAdmin && !viewAs && (
            <div style={{ padding: '0 6px 10px' }}>
              <label
                className="muted"
                style={{ display: 'block', fontSize: 10, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4 }}
              >
                Preview as role
              </label>
              <select
                className="select"
                value={previewRole || ''}
                onChange={(e) => { setPreviewRole(e.target.value || null); setOpen(false); }}
                title="Render the app as this role would see it — visual only, your real permissions are unchanged"
                style={{ width: '100%', fontSize: 12, padding: '6px 28px 6px 8px' }}
              >
                <option value="">Off (admin view)</option>
                {previewableRoles.map((r) => (
                  <option key={r} value={r}>
                    {r.charAt(0).toUpperCase() + r.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            type="button"
            className="btn ghost sm"
            style={{ width: '100%', justifyContent: 'flex-start' }}
            onClick={() => {
              setOpen(false);
              if (window.confirm('Sign out of Docket?')) logout();
            }}
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
          <VersionBadge collapsed={false} />
        </div>
      )}
      <div
        className="sidebar-user"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((v) => !v);
          }
        }}
        title={collapsed ? (user?.name || 'Account') : 'Account menu'}
        role="button"
        tabIndex={0}
        aria-label="Account menu"
        aria-expanded={open}
      >
        {user?.avatar_url && !avatarFailed ? (
          <img
            src={user.avatar_url}
            alt=""
            referrerPolicy="no-referrer"
            onError={() => setAvatarFailed(true)}
            style={{ width: 26, height: 26, borderRadius: 999, flexShrink: 0, objectFit: 'cover' }}
          />
        ) : (
          <div
            role="img"
            aria-label={user?.name || 'User avatar'}
            style={{
              width: 26, height: 26, borderRadius: 999,
              background: 'var(--surface-purple)', color: 'var(--text-purple)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 500, flexShrink: 0,
            }}
          >
            {initials}
          </div>
        )}
        {!collapsed && (
          <div className="sidebar-user-info">
            <div className="sidebar-user-name">{user?.name || 'User'}</div>
            <div className="sidebar-user-email">{user?.email}</div>
          </div>
        )}
        {!collapsed && (
          <ChevronUp
            className="w-[14px] h-[14px] shrink-0"
            style={{ opacity: 0.5, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 140ms' }}
          />
        )}
      </div>
    </div>
  );
}

// `collapseLocked` means the viewport (not the user) is forcing the collapsed
// state, so the expand affordance is withheld: clicking it would flip the
// stored preference while the sidebar stayed shut, i.e. a button that looks
// dead. The brand mark still renders, just not as a control.
function Sidebar({ collapsed, setCollapsed, collapseLocked = false }) {
  const { user, logout, can, realIsAdmin, previewRole, setPreviewRole, previewableRoles, viewAs, exitViewAs } = useAuth();
  const { isDark } = useTheme();

  // Split into two sections so Admin tools are visually separated from
  // everyday work. Upload moved to the admin block — only legal/admin
  // can create new contracts. Non-legal users still append files to an
  // existing contract via the contract-detail page (markups, revisions).
  const workspaceNav = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { to: '/contracts', icon: FileSignature, label: 'Contracts' },
    { to: '/counterparties', icon: Building2, label: 'Counterparties' },
    { to: '/intake', icon: RouteIcon, label: 'Intake' },
    { to: '/tprm', icon: Handshake, label: 'TPRM' },
    { to: '/reviews', icon: ShieldCheck, label: 'Reviews' },
    // Documentation is visible to every signed-in user (doc-277) — no
    // capability gate. Which docs exist is src/docs/index.js's business.
    { to: '/docs', icon: BookText, label: 'Documentation' },
  ];
  // Grouped by purpose: day-to-day work first (upload, intakes, playbooks),
  // then oversight (assistant conversations, activity log), then configuration
  // (user roles, settings) last.
  const adminNav = [
    ...(can('upload_contracts') ? [{ to: '/contracts/upload', icon: Upload, label: 'Upload' }] : []),
    ...(can('manage_intakes') ? [{ to: '/admin/intakes', icon: RouteIcon, label: 'Intakes' }] : []),
    ...(can('write_contracts') ? [{ to: '/admin/playbooks', icon: BookOpen, label: 'Playbooks' }] : []),
    ...(can('manage_schemas') ? [{ to: '/admin/schemas', icon: Braces, label: 'Data Schemas' }] : []),
    ...(can('view_conversations') ? [{ to: '/admin/conversations', icon: MessageSquare, label: 'Conversations' }] : []),
    ...(can('view_audit') ? [{ to: '/admin/activity', icon: ActivityIcon, label: 'Activity' }] : []),
    ...(can('manage_roles') ? [{ to: '/admin/users', icon: UsersIcon, label: 'User Roles' }] : []),
    ...(can('manage_settings') ? [{ to: '/admin/settings', icon: HardDrive, label: 'Settings' }] : []),
  ];
  const allPaths = [...workspaceNav, ...adminNav].map((n) => n.to);

  const initials = (user?.name || user?.email || '?').charAt(0).toUpperCase();
  // Google's avatar URLs occasionally fail (rotated profile pic, rate
  // limit, ad-blocker). Track the failure so we can swap to the
  // initials placeholder instead of showing a broken-image icon. Reset
  // whenever the URL itself changes (e.g. re-login).
  const [avatarFailed, setAvatarFailed] = useState(false);
  useEffect(() => { setAvatarFailed(false); }, [user?.avatar_url]);

  const sidebarContent = (
    <>
      <div
        className="sidebar-top"
        style={collapsed ? { justifyContent: 'center', padding: 0 } : undefined}
      >
        {collapsed ? (
          collapseLocked ? (
            <img
              src={isDark ? '/lightspark-logo-white.svg' : '/lightspark-logo.svg'}
              alt="Docket"
              style={{ width: 22, height: 22, display: 'block', padding: 6, boxSizing: 'content-box' }}
            />
          ) : (
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              title="Expand sidebar"
              style={{
                background: 'transparent',
                border: 0,
                padding: 6,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={isDark ? '/lightspark-logo-white.svg' : '/lightspark-logo.svg'}
                alt="Expand sidebar"
                style={{ width: 22, height: 22, display: 'block' }}
              />
            </button>
          )
        ) : (
          <>
            <Brand isDark={isDark} />
            <button
              className="icon-btn xs"
              onClick={() => setCollapsed(true)}
              title="Collapse sidebar"
              aria-label="Collapse sidebar"
              style={{ marginLeft: 'auto' }}
            >
              <PanelLeft className="w-[14px] h-[14px]" />
            </button>
          </>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        <div className="sidebar-section">
          {!collapsed && <div className="sidebar-section-label">Workspace</div>}
          {workspaceNav.map((item) => (
            <SidebarItem
              key={item.to}
              {...item}
              collapsed={collapsed}
              siblingPaths={allPaths}
            />
          ))}
        </div>
        {adminNav.length > 0 && (
          <div className="sidebar-section">
            {!collapsed && <div className="sidebar-section-label">Admin</div>}
            {adminNav.map((item) => (
              <SidebarItem
                key={item.to}
                {...item}
                collapsed={collapsed}
                siblingPaths={allPaths}
              />
            ))}
          </div>
        )}
      </div>

      <div className="sidebar-bottom">
        {/* View-as (doc-323) is the server-side lens and outranks the
            client-side role preview, so it takes the one banner slot. Same
            markup and same tokens as the preview badge below — doc-267 tuned
            those for light-mode contrast, so keep them in step. */}
        {viewAs && (
          <div
            className="row"
            style={{
              gap: 6, alignItems: 'center', marginBottom: 8,
              padding: collapsed ? '4px 0' : '4px 8px',
              justifyContent: collapsed ? 'center' : 'space-between',
              borderRadius: 6,
              background: 'var(--surface-alpha-primary)',
              border: '0.5px solid var(--text-purple)',
              fontSize: 11, color: 'var(--text-purple)',
            }}
            title={`Viewing as ${viewAs.target?.name || viewAs.target?.email} (${viewAs.target?.email}) — read-only`}
          >
            <span className="row" style={{ gap: 5, alignItems: 'center', minWidth: 0 }}>
              <Eye className="w-3 h-3 shrink-0" />
              {!collapsed && (
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {viewAs.target?.name || viewAs.target?.email} · read-only
                </span>
              )}
            </span>
            {!collapsed && (
              <button
                type="button"
                onClick={exitViewAs}
                title="Exit view-as"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex', padding: 0 }}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
        {!viewAs && previewRole && (
          <div
            className="row"
            style={{
              gap: 6, alignItems: 'center', marginBottom: 8,
              padding: collapsed ? '4px 0' : '4px 8px',
              justifyContent: collapsed ? 'center' : 'space-between',
              borderRadius: 6,
              background: 'var(--surface-alpha-primary)',
              border: '0.5px solid var(--text-purple)',
              fontSize: 11, color: 'var(--text-purple)',
            }}
            title={`Previewing as ${previewRole} — your real permissions are unchanged`}
          >
            <span className="row" style={{ gap: 5, alignItems: 'center', minWidth: 0 }}>
              <Eye className="w-3 h-3 shrink-0" />
              {!collapsed && (
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {previewRole.charAt(0).toUpperCase() + previewRole.slice(1)}
                </span>
              )}
            </span>
            {!collapsed && (
              <button
                type="button"
                onClick={() => setPreviewRole(null)}
                title="Exit preview"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex', padding: 0 }}
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
        <UserMenu
          user={user}
          initials={initials}
          avatarFailed={avatarFailed}
          setAvatarFailed={setAvatarFailed}
          collapsed={collapsed}
          realIsAdmin={realIsAdmin}
          previewRole={previewRole}
          setPreviewRole={setPreviewRole}
          previewableRoles={previewableRoles}
          viewAs={viewAs}
          logout={logout}
        />
      </div>
    </>
  );

  return (
    <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`} style={{ display: 'flex' }}>
      {sidebarContent}
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/* Topbar — breadcrumbs + search trigger + quick icons                 */
/* ------------------------------------------------------------------ */

function useBreadcrumbs() {
  const location = useLocation();
  return useMemo(() => {
    const path = location.pathname;
    // Known routes → static breadcrumb trails. Dynamic segments (IDs) use
    // "Loading…" until the relevant page sets a better title via the
    // `data-page-title` attribute on its H1 (picked up by the effect below).
    if (path === '/') return [{ label: 'Dashboard' }];
    if (path === '/contracts') return [{ label: 'Contracts' }];
    if (path === '/contracts/upload') return [
      { label: 'Contracts', to: '/contracts' },
      { label: 'Upload' },
    ];
    if (path === '/intake') return [{ label: 'Intake' }];
    if (/^\/intake\/sessions\/[^/]+$/.test(path)) return [
      { label: 'Intake', to: '/intake' },
      { label: 'Detail' },
    ];
    if (/^\/intake\/[^/]+$/.test(path)) return [
      { label: 'Intake', to: '/intake' },
      { label: 'New' },
    ];
    if (/^\/intake\/[^/]+\/[^/]+$/.test(path)) return [
      { label: 'Intake', to: '/intake' },
      { label: 'Resume' },
    ];
    if (/^\/contracts\/[^/]+$/.test(path)) return [
      { label: 'Contracts', to: '/contracts' },
      { label: 'Detail' },
    ];
    if (path === '/tprm') return [{ label: 'TPRM' }];
    if (path === '/tprm/new') return [
      { label: 'TPRM', to: '/tprm' },
      { label: 'New' },
    ];
    if (/^\/tprm\/[^/]+$/.test(path)) return [
      { label: 'TPRM', to: '/tprm' },
      { label: 'Detail' },
    ];
    if (/^\/reviews\/[^/]+$/.test(path)) return [
      { label: 'Reviews', to: '/reviews' },
      { label: 'Detail' },
    ];
    if (path === '/reviews') return [{ label: 'Reviews' }];
    if (path === '/admin/settings') return [
      { label: 'Admin' },
      { label: 'Settings' },
    ];
    if (path === '/admin/playbooks') return [
      { label: 'Admin' },
      { label: 'Review Playbooks' },
    ];
    if (path === '/admin/schemas') return [
      { label: 'Admin' },
      { label: 'Data Schemas' },
    ];
    if (path === '/admin/intakes') return [
      { label: 'Admin' },
      { label: 'Intakes' },
    ];
    if (path === '/admin/conversations') return [
      { label: 'Admin' },
      { label: 'Conversations' },
    ];
    if (path === '/admin/users') return [
      { label: 'Admin' },
      { label: 'User Roles' },
    ];
    if (path === '/admin/activity') return [
      { label: 'Admin' },
      { label: 'Activity' },
    ];
    return [{ label: 'Docket' }];
  }, [location.pathname]);
}

function Topbar() {
  const crumbs = useBreadcrumbs();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  return (
    <div className="topbar">
      <div className="crumbs">
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <ChevronRight className="sep w-3 h-3" />}
            {i === crumbs.length - 1 ? (
              <span className="current">{c.label}</span>
            ) : (
              <button onClick={() => c.to && navigate(c.to)}>{c.label}</button>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Search trigger opens the global ⌘K command palette. */}
      <button
        className="search-trigger"
        onClick={() => document.dispatchEvent(new CustomEvent('commandk:open'))}
      >
        <Search className="w-[14px] h-[14px]" />
        <span>Search contracts, counterparties…</span>
        <span className="search-trigger-kbd">⌘K</span>
      </button>

      <button
        className="icon-btn"
        onClick={toggleTheme}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Shell                                                                */
/* ------------------------------------------------------------------ */

function AppContent() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebar:collapsed') === '1';
  });
  const { user, loading } = useAuth();
  const location = useLocation();

  // Below ~700px the expanded 240px sidebar leaves too little canvas for the
  // dashboard tiles to fit, so narrowness FORCES the collapsed rendering. It is
  // ORed with the stored preference rather than written into it — the effect
  // below stays the only writer of localStorage, so widening the window again
  // restores whatever the user actually chose.
  const isNarrowViewport = useNarrowViewport();
  const sidebarIsCollapsed = sidebarCollapsed || isNarrowViewport;

  useEffect(() => {
    localStorage.setItem('sidebar:collapsed', sidebarCollapsed ? '1' : '0');
  }, [sidebarCollapsed]);

  useEffect(() => {
    const titles = {
      '/': 'Dashboard — Docket',
      '/contracts': 'Contracts — Docket',
      '/contracts/upload': 'Upload Contract — Docket',
      '/tprm': 'TPRM — Docket',
      '/tprm/new': 'New TPRM request — Docket',
      '/intake': 'Intake — Docket',
      '/admin/conversations': 'Assistant Conversations — Docket',
      '/admin/settings': 'Admin Settings — Docket',
      '/admin/playbooks': 'Review Playbooks — Docket',
      '/admin/schemas': 'Data Schemas — Docket',
      '/reviews': 'Document Reviews — Docket',
      '/admin/users': 'User Roles — Docket',
      '/admin/activity': 'Activity — Docket',
      '/login': 'Sign in — Docket',
    };
    // /intake/:type and /intake/sessions/:id get specific titles.
    const path = location.pathname;
    if (/^\/intake\/sessions\/[^/]+$/.test(path)) {
      document.title = 'Intake — Docket';
    } else if (/^\/intake\/[^/]+(\/[^/]+)?$/.test(path)) {
      document.title = 'New intake — Docket';
    } else {
      document.title = titles[path] || 'Docket';
    }
  }, [location]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className="shell">
      <Sidebar
        collapsed={sidebarIsCollapsed}
        setCollapsed={setSidebarCollapsed}
        collapseLocked={isNarrowViewport}
      />
      <main className="main">
        <Topbar />
        <div className="canvas">
          <Routes>
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/contracts" element={<ProtectedRoute><ContractsList /></ProtectedRoute>} />
            <Route path="/contracts/upload" element={<CapabilityRoute cap="upload_contracts"><UploadContract apiBase={API_BASE} /></CapabilityRoute>} />
            <Route path="/admin/playbooks" element={<LegalRoute><Playbooks /></LegalRoute>} />
            <Route path="/admin/schemas" element={<CapabilityRoute cap="manage_schemas"><DataSchemas /></CapabilityRoute>} />
            {/* Legacy settings paths → consolidated /admin/* */}
            <Route path="/settings/review" element={<Navigate to="/admin/playbooks" replace />} />
            <Route path="/settings/drive" element={<Navigate to="/admin/settings" replace />} />
            <Route path="/contracts/new" element={<LegalRoute><ContractForm /></LegalRoute>} />
            <Route path="/intake" element={<ProtectedRoute><Generate /></ProtectedRoute>} />
            <Route path="/intake/sessions/:id" element={<ProtectedRoute><ScreeningDetail /></ProtectedRoute>} />
            <Route path="/intake/:type" element={<ProtectedRoute><Generate /></ProtectedRoute>} />
            <Route path="/intake/:type/:sessionId" element={<ProtectedRoute><Generate /></ProtectedRoute>} />
            <Route path="/contracts/:id" element={<ProtectedRoute><ContractDetail apiBase={API_BASE} /></ProtectedRoute>} />
            <Route path="/contracts/:id/edit" element={<LegalRoute><ContractForm /></LegalRoute>} />
            <Route path="/counterparties" element={<ProtectedRoute><CounterpartiesList /></ProtectedRoute>} />
            <Route path="/counterparties/:key" element={<ProtectedRoute><CounterpartyDetail /></ProtectedRoute>} />
            <Route path="/tprm" element={<ProtectedRoute><RequestsList /></ProtectedRoute>} />
            <Route path="/tprm/new" element={<ProtectedRoute><RequestForm /></ProtectedRoute>} />
            <Route path="/tprm/:id/edit" element={<ProtectedRoute><RequestForm /></ProtectedRoute>} />
            <Route path="/tprm/:id" element={<ProtectedRoute><RequestDetail apiBase={API_BASE} /></ProtectedRoute>} />
            <Route path="/reviews" element={<ProtectedRoute><ReviewsList /></ProtectedRoute>} />
            <Route path="/reviews/:id" element={<ProtectedRoute><ReviewDetail /></ProtectedRoute>} />
            <Route path="/admin/intakes" element={<ProtectedRoute><AdminIntakes /></ProtectedRoute>} />
            <Route path="/admin/conversations" element={<ProtectedRoute><AdminConversations /></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute><DriveSettings /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute><AdminUsers /></ProtectedRoute>} />
            <Route path="/admin/activity" element={<ProtectedRoute><AdminActivity /></ProtectedRoute>} />
            <Route path="/docs" element={<ProtectedRoute><Docs /></ProtectedRoute>} />
            <Route path="/docs/:slug" element={<ProtectedRoute><DocPage /></ProtectedRoute>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </main>
      {/* Global ⌘K command palette — mounted once, renders its own modal
          when opened. Does nothing if not triggered. */}
      <CommandK />
    </div>
  );
}

function NotFound() {
  return (
    <div className="canvas-inner">
      <div
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: '80px 0', textAlign: 'center',
        }}
      >
        <div style={{
          width: 64, height: 64, borderRadius: 999,
          display: 'grid', placeItems: 'center', marginBottom: 24,
          background: 'var(--surface-secondary)',
        }}>
          <FileText className="w-7 h-7" style={{ color: 'var(--icon-secondary)' }} />
        </div>
        <h1 className="page-title" style={{ marginBottom: 8 }}>Page not found</h1>
        <p className="page-subtitle" style={{ marginBottom: 24 }}>The page you're looking for doesn't exist.</p>
        <Link to="/" className="btn primary">Go to Dashboard</Link>
      </div>
    </div>
  );
}

// A data router (vs. plain <BrowserRouter>) so pages can use `useBlocker`
// for unsaved-changes guards. Routes stay declared as descendant <Routes>
// inside AppContent; the single splat route just puts the whole tree under
// the data router's navigation pipeline.
const router = createBrowserRouter([
  {
    path: '*',
    element: (
      <AuthProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </AuthProvider>
    ),
  },
]);

function App() {
  return (
    <ThemeProvider>
      <RouterProvider router={router} />
    </ThemeProvider>
  );
}

export default App;
